"use client";

import { ExternalLink, MapPin } from "lucide-react";

import { FadeIn } from "@/components/ui/motion";
import { useLang, useLangKey } from "@/contexts/lang";
import type { Hotel, SectionIds } from "@/data/home";

export const FEATURED = [
	"Holiday Inn Скопје",
	"Хотел Турист",
	"Хотел Карпош",
	"Хотел Bellevue",
];

type PortfolioSectionProps = {
	sectionIds: SectionIds;
	hotels: Hotel[];
};

export function PortfolioSection({
	sectionIds,
	hotels,
}: PortfolioSectionProps) {
	const t = useLang();
	const featured = FEATURED.flatMap((name) => {
		const h = hotels.find((x) => x.name === name);
		return h ? [h] : [];
	});

	return (
		<section
			id={sectionIds.portfolio}
			className="scroll-mt-32 overflow-hidden bg-primary py-16 sm:py-20 md:py-28 mt-grain"
		>
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				{/* ── Header ── */}
				<FadeIn>
					<div className="mb-10 sm:mb-14">
						<div className="flex items-center gap-4 mb-4 sm:mb-5">
							<span className="h-px w-10 sm:w-16 bg-accent/60" />
							<p className="text-label text-accent">{t.portfolio.label}</p>
						</div>
						<h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal text-white leading-[1.1] tracking-tight mb-5 md:mb-6">
							{t.portfolio.heading1}
							<span className="block mt-1 font-semibold text-white/60">
								{t.portfolio.heading2}
							</span>
						</h2>
						<p className="text-base sm:text-lg text-white/65 leading-relaxed max-w-2xl">
							{t.portfolio.description}
						</p>
					</div>
				</FadeIn>

				{/* ── 4-column grid ── */}
				<div className="-mx-4 sm:-mx-6 lg:mx-0 overflow-x-auto px-4 sm:px-6 lg:px-0 pb-2">
					<div className="grid grid-cols-4 gap-5 min-w-[800px] lg:min-w-0">
						{featured.map((hotel) => (
							<HotelCard key={hotel.name} hotel={hotel} />
						))}
					</div>
				</div>
			</div>
		</section>
	);
}

// ─── Card ─────────────────────────────────────────────────────────────────────

export function HotelCard({ hotel }: { hotel: Hotel }) {
	const t = useLang();
	const lang = useLangKey();
	const name = lang === "en" ? hotel.nameEn : hotel.name;
	const description = lang === "en" ? hotel.descriptionEn : hotel.description;
	const category = lang === "en" ? hotel.categoryEn : hotel.category;

	return (
		<article className="group flex flex-col overflow-hidden rounded-sm bg-card shadow-luxury transition-all duration-500 hover:-translate-y-1.5 hover:shadow-luxury-xl">
			{/* ── Image zone ── */}
			<div className="relative aspect-[16/10] shrink-0 overflow-hidden bg-secondary/30">
				{hotel.image ? (
					<img
						src={hotel.image}
						alt={hotel.name}
						className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
					/>
				) : (
					<div className="absolute inset-0 flex items-center justify-center bg-primary/10">
						<span className="font-display text-4xl text-primary/10">
							{hotel.name.charAt(0)}
						</span>
					</div>
				)}

				{/* Location badge */}
				<div className="absolute left-3.5 top-3.5">
					<span className="inline-flex items-center gap-1.5 rounded-sm bg-white/95 px-2.5 py-1 text-[10px] font-semibold text-primary shadow-luxury-sm backdrop-blur-sm">
						<MapPin className="h-2.5 w-2.5 text-accent" />
						{t.portfolio.locationBadge}
					</span>
				</div>

				{/* Hover gold tint */}
				<div className="pointer-events-none absolute inset-0 bg-accent/0 transition-colors duration-700 group-hover:bg-accent/8" />
			</div>

			{/* ── Content zone ── */}
			<div className="flex flex-1 flex-col p-5 sm:p-6">
				{/* Gold accent line */}
				<div className="mb-4 h-px w-8 bg-accent/50 transition-all duration-500 group-hover:w-12 group-hover:bg-accent" />

				{/* Category */}
				<p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-accent">
					{category}
				</p>

				{/* Name */}
				<h3 className="font-display text-xl sm:text-2xl font-normal leading-snug tracking-tight text-primary mb-3">
					{name}
				</h3>

				{/* Description */}
				<p className="line-clamp-3 text-sm leading-relaxed text-foreground/85 flex-1 mb-5">
					{description}
				</p>

				{/* Actions */}
				<div className="flex items-center gap-2 pt-4 border-t border-border mt-auto">
					<button
						type="button"
						className="inline-flex flex-1 cursor-pointer items-center justify-center rounded-sm bg-primary px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-white transition-all duration-300 hover:bg-primary/90"
					>
						{t.portfolio.reserve}
					</button>
					<button
						type="button"
						className="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-sm border border-border px-3.5 py-2 text-[11px] font-semibold uppercase tracking-wider text-primary/70 transition-all duration-300 hover:border-primary/20 hover:text-primary"
					>
						<ExternalLink className="h-3 w-3" />
					</button>
				</div>
			</div>
		</article>
	);
}
